import { Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarDays, Clock, FileText, Mail, MapPin, Phone, Sparkles, Users } from "lucide-react";
import {
  useEventDetail,
  useUpdateEventStatus,
  useUpdateEventOutcome,
  EVENT_TYPE_LABELS,
  EVENT_STATUS_LABELS,
  EVENT_STATUS_BADGE,
  EVENT_OUTCOME_LABELS,
  EVENT_OUTCOME_BADGE,
  type EventStatus,
  type EventOutcome,
} from "@/hooks/useEvents";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { reportUpdateFailure } from "@/lib/event-update-failure";
import { EventStatusSelector } from "@/components/admin/events/EventStatusSelector";
import { EventOutcomeSelector } from "@/components/admin/events/EventOutcomeSelector";
import { EventStatusHistoryTimeline } from "@/components/admin/events/EventStatusHistoryTimeline";

/**
 * One event's details, status and outcome in a side sheet (moved from the old
 * Events List page, #1557). The List view opens it from a row.
 */
export function EventDetailSheet({ eventId, onClose }: { eventId: string | null; onClose: () => void }) {
  const { data: detail } = useEventDetail(eventId);
  const updateStatus = useUpdateEventStatus();
  const updateOutcome = useUpdateEventOutcome();

  const handleStatusChange = async (eventId: string, from: EventStatus, next: EventStatus, note?: string) => {
    try {
      await updateStatus.mutateAsync({ id: eventId, fromStatus: from, status: next, note });
      toast.success(`Status updated to ${EVENT_STATUS_LABELS[next]}`);
    } catch (err) {
      reportUpdateFailure(err, "Failed to update status", "status", eventId);
    }
  };

  const handleOutcomeChange = async (eventId: string, outcome: EventOutcome) => {
    try {
      await updateOutcome.mutateAsync({ id: eventId, outcome });
      toast.success(`Outcome updated to ${EVENT_OUTCOME_LABELS[outcome]}`);
    } catch (err) {
      reportUpdateFailure(err, "Failed to update outcome", "outcome", eventId);
    }
  };

  return (
    <Sheet
      open={!!eventId}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <SheetContent className="sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            {detail?.event_number ?? "Loading..."}
          </SheetTitle>
          {detail && (
            <Link to={`/admin/events/${detail.id}`} className="text-sm text-primary hover:underline">
              Open event page
            </Link>
          )}
        </SheetHeader>

        {detail && (
          <div className="mt-6 space-y-6">
            {/* Status + type + outcome header */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="capitalize text-xs">
                  {EVENT_TYPE_LABELS[detail.event_type]}
                </Badge>
                <Badge
                  variant="outline"
                  className={`${EVENT_STATUS_BADGE[detail.status]} capitalize text-sm px-3 py-1`}
                >
                  {EVENT_STATUS_LABELS[detail.status]}
                </Badge>
                <Badge
                  variant="outline"
                  className={`${EVENT_OUTCOME_BADGE[detail.outcome]} capitalize text-sm px-3 py-1`}
                >
                  {EVENT_OUTCOME_LABELS[detail.outcome]}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <EventOutcomeSelector
                  currentOutcome={detail.outcome}
                  onChange={(outcome) => handleOutcomeChange(detail.id, outcome)}
                  size="sm"
                />
                <EventStatusSelector
                  currentStatus={detail.status}
                  onChange={(next, note) => handleStatusChange(detail.id, detail.status, next, note)}
                  size="sm"
                />
              </div>
            </div>

            {/* Client */}
            {(detail.client_name || detail.client_email || detail.client_phone) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Client</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {detail.client_name && <p className="font-medium text-foreground">{detail.client_name}</p>}
                  {detail.client_email && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" /> {detail.client_email}
                    </p>
                  )}
                  {detail.client_phone && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-3.5 w-3.5" /> {detail.client_phone}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Event details */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Event</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {detail.event_date && (
                  <p className="flex items-center gap-2">
                    <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />{" "}
                    {format(new Date(detail.event_date), "MMMM d, yyyy")}
                  </p>
                )}
                {detail.event_time && (
                  <p className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" /> {detail.event_time}
                  </p>
                )}
                {detail.guest_count !== null && detail.guest_count !== undefined && (
                  <p className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" /> {detail.guest_count} guests
                  </p>
                )}
                {detail.fulfillment_type && (
                  <p className="flex items-center gap-2 capitalize">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {detail.fulfillment_type}
                  </p>
                )}
                {detail.delivery_address && <p className="pl-5 text-muted-foreground">{detail.delivery_address}</p>}
                {detail.is_recurring_corporate && (
                  <p className="text-amber-700 dark:text-amber-300 font-medium">
                    Recurring corporate order — route to sales
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Line items (if any) */}
            {detail.items && detail.items.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Line Items</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {detail.items.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span>
                        {item.quantity}× {item.name}{" "}
                        <span className="text-muted-foreground">({item.label || item.pricing_type})</span>
                      </span>
                      <span className="font-medium">${item.line_total.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-border pt-2 flex justify-between font-semibold">
                    <span>Subtotal</span>
                    <span>${detail.subtotal.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Customer notes */}
            {detail.notes && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Customer Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{detail.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Status history */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Status History</CardTitle>
              </CardHeader>
              <CardContent>
                <EventStatusHistoryTimeline eventId={detail.id} />
              </CardContent>
            </Card>

            {/* Source attribution */}
            <div className="text-xs text-muted-foreground space-y-1">
              {detail.source && (
                <p>
                  Source: <span className="font-mono">{detail.source}</span>
                  {detail.source_page_slug ? ` / ${detail.source_page_slug}` : ""}
                </p>
              )}
              <p>Created {format(new Date(detail.created_at), "MMM d, yyyy 'at' h:mm a")}</p>
              {detail.ghl_opportunity_id && (
                <p>
                  GHL opportunity: <span className="font-mono">{detail.ghl_opportunity_id}</span>
                </p>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
